import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { useLocalStorage } from "@mantine/hooks";
import {
  type FC,
  type ReactElement,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { router } from "@/router.ts";

const LocaleContext = createContext({
  locale: "en",
  setLocale: (_locale: string) => {},
});

export function useLocale() {
  return useContext(LocaleContext);
}

const AVAILABLE_LOCALES = ["en", "sv"];
const defaultLocale =
  navigator.languages
    .find((l) => AVAILABLE_LOCALES.includes(l.split("-")[0]))
    ?.split("-")[0] ?? "en";

const LocaleProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [locale, setLocale] = useLocalStorage({
    key: "reality-locale",
    defaultValue: defaultLocale,
  });

  const [translationsLoaded, setTranslationsLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const { messages } = await import(`./${locale}/merged.ts`);

      i18n.load(locale, messages);
      i18n.activate(locale);
      setTranslationsLoaded(true);

      // forces a refresh of title tag translations
      await router.invalidate();
    })();
  }, [locale]);

  if (!translationsLoaded) {
    return null;
  }

  return (
    <I18nProvider i18n={i18n}>
      <LocaleContext value={{ locale, setLocale }}>{children}</LocaleContext>
    </I18nProvider>
  );
};

export default LocaleProvider;
